import { FORM_ERROR } from 'final-form'
import __isObject from 'lodash/isObject'
import type { Action, Dispatch } from 'redux'

import {
  getCommonErrorMessage,
  getCommonFormErrors,
  getErrorMessageFromData,
  NETWORK_ERROR_MESSAGE_PLACEHOLDER,
} from '@libs/typescript-utils'

import { getDateFilter } from './cookie'
import { getFirstDayOfTheYear, getLastDayOfTheYear } from './date'

import { NetworkErrorMessage } from '@messages'

export function getIdsFromList(list: Array<{ id: ItemIdType }>) {
  return list.map(d => d.id)
}

export function bindActionToPromise<Payload, Results>(
  dispatch: Dispatch,
  action: (
    payload: Payload,
    meta: {
      resolve: (value: Results | PromiseLike<Results>) => void
      reject: (reason?: unknown) => void
    }
  ) => Action
) {
  return function actionCreator(payload: Payload) {
    return new Promise<Results>((resolve, reject) => dispatch(action(payload, { resolve, reject })))
  }
}

export function getErrorMessage(error: unknown): string[] | StringOrMessage {
  const msg = getCommonErrorMessage(error)
  return msg === NETWORK_ERROR_MESSAGE_PLACEHOLDER ? NetworkErrorMessage : msg
}

export function getErrorMessageFromBlob({
  message,
  response: { data: blob },
}: {
  message?: string
  response: { data: Blob }
}) {
  try {
    const promise = new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.addEventListener('abort', reject)
      reader.addEventListener('error', reject)
      reader.addEventListener('loadend', () => {
        resolve(reader.result)
      })
      reader.readAsText(blob)
    })

    return promise.then(json => {
      const data = JSON.parse(String(json))
      if (data && __isObject(data)) {
        return getErrorMessageFromData(data)
      }
      return message
    })
  } catch (_error) {
    // ignore error
    return message
  }
}

function parseRFFFormErrors(errors: string | Record<string, StringOrMessage | string[] | undefined> | undefined) {
  if (typeof errors === 'string') {
    return { [FORM_ERROR]: errors }
  }
  // this is the old way that backend handles non field errors
  if (__isObject(errors)) {
    if (errors['_error']) {
      errors[FORM_ERROR] = errors['_error']
      delete errors['_error']
    }
    // this is the new way that backend handles non field errors
    if (errors['non_field_errors']) {
      errors[FORM_ERROR] = errors['non_field_errors']
      delete errors['non_field_errors']
    }
    if (errors['Error']) {
      errors[FORM_ERROR] = errors['Error']
      delete errors['Error']
    }
  }
  return errors
}

export function getFormErrors(error: unknown) {
  const errors = getCommonFormErrors(error)
  if (typeof errors === 'object' && '_error' in errors && errors['_error'] === NETWORK_ERROR_MESSAGE_PLACEHOLDER) {
    errors['_error'] = NetworkErrorMessage
  }
  return errors
}

//* UTIL for migrate redux-form > react-final-form
export function getRFFFormErrors(error: unknown) {
  const errors = getFormErrors(error)

  return parseRFFFormErrors(errors)
}

function getPaginationOptionsFromStore<StoreState extends { page: number; pageSize: number }>({
  page,
  pageSize,
}: StoreState) {
  return {
    page,
    pageSize,
  }
}

// TODO: del this once all services are migrated to service v2
function getOrderingFromStore<StoreState extends { order: OrderOptions; orderBy: string }>({
  order,
  orderBy,
}: StoreState) {
  const prefix = order === 'asc' ? '' : '-'
  return prefix + orderBy
}

//! NOTE: mixed with v1 and v2 services - using v2 UI with v1 API
export function getPartnerParamsFromStore(state: Store, { withType = false, withPage = false } = {}) {
  const {
    fromDate,
    toDate,
    partnerList: { search, type },
  } = state.filters

  const { page, pageSize } = getPaginationOptionsFromStore(state.partners)
  const ordering = getOrderingFromStore(state.partners)
  const params: Record<string, unknown> = {
    fromDate,
    toDate,
    search,
    pageSize,
    ordering,
  }

  if (withType) {
    params.type = type
  }

  if (withPage) {
    params.page = page
  }

  return cleanQueryParams(params) as unknown as PartnerPageParams
}

function cleanQueryParams(params: Record<string, unknown>) {
  if (__isObject(params)) {
    const tmp = {} as Record<string, unknown>
    for (const key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key) && params[key] !== '' && params[key] !== false) {
        tmp[key] = params[key]
      }
    }
    return tmp
  }
  return params
}

export function getCompanyType(state: Store, companyTypeId?: string): Nullable<string> {
  if (!companyTypeId) {
    return null
  }

  return state.dashboard.common.company_types.find(({ id }) => id === companyTypeId)?.name ?? null
}

function getDate(dates: Array<string | null | undefined>) {
  return dates.find(date => date != null)
}

// this method is pass filters to dashboard and datepickers
export function getInitialDateFilters() {
  return {
    fromDate: getDate([getDateFilter('fromDate'), getFirstDayOfTheYear()]),
    toDate: getDate([getDateFilter('toDate'), getLastDayOfTheYear()]),
  }
}

export function generateBulkRemoveAPIPayload(
  payload:
    | { selectedInvoices: number[]; isAllInvoiceSelected: boolean }
    | { selectedSalaries: number[]; isAllSalarySelected: boolean }
    | { selectedTaxes: number[]; isAllTaxSelected: boolean },
  filters: Record<string, unknown>,
  dataType: 'expense' | 'tax'
) {
  const { tags, ...params } = filters

  const apiPayload = {} as { data: unknown; params: Record<string, unknown> }

  if (dataType === 'expense') {
    const { selectedInvoices, isAllInvoiceSelected } = payload as {
      selectedInvoices: number[]
      isAllInvoiceSelected: boolean
    }
    apiPayload.data = {
      invoice_ids: isAllInvoiceSelected ? 'all' : selectedInvoices,
    }
    // only send filters when isAllInvoiceSelected is true
    if (isAllInvoiceSelected) {
      apiPayload.params = params
    }
  } else {
    // tax
    const { selectedTaxes, isAllTaxSelected } = payload as {
      selectedTaxes: number[]
      isAllTaxSelected: boolean
    }
    apiPayload.data = {
      monthly_tax_ids: isAllTaxSelected ? 'all' : [],
      tax_ids: selectedTaxes,
    }
    // only send filters when isAllInvoiceSelected is true
    if (isAllTaxSelected) {
      apiPayload.params = params
    }
  }

  return apiPayload
}

export function getActiveCompanyId(state: Store) {
  return state.auth.company.data.id
}

export function getActiveCompany(state: Store): Company {
  return state.auth.company.data
}

export function getDefaultUserCompanyId(state: Store) {
  const companies = state.auth.companies
  // select when user has only one company
  if (companies.length === 1) {
    return companies[0].id
  }
  return null
}

//* INCOME
export function getIncomeTypes(state: Store) {
  return state.dashboard.common.income_types
}
export function getIncomeTypeText({ income_type }: IncomeDetailsFrontendValues, options: CommonValueAndLabelType[]) {
  return options.find(({ value }) => value === income_type)?.label ?? null
}
